import { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpService } from "../../utils/DesigningHttpService.js";
import { getAgencySession } from "../../utils/agencySession.ts";
import AppFeedback from "../../components/shared/AppFeedback.tsx";
import { activateLanguage, configureLanguages, t, useStoreLanguage, type StoreLanguage } from "./i18n.ts";
import iranFlag from "flag-icons/flags/4x3/ir.svg";
import britainFlag from "flag-icons/flags/4x3/gb.svg";
import saudiFlag from "flag-icons/flags/4x3/sa.svg";
import "./language.css";

const http = new HttpService();
const flags: Record<string, string> = { ir: iranFlag, gb: britainFlag, sa: saudiFlag };
const publicLoginLanguages: StoreLanguage[] = [
  { code: "fa", nativeName: "فارسی", direction: "rtl", flagCountryCode: "ir", isDefault: true },
  { code: "en", nativeName: "English", direction: "ltr", flagCountryCode: "gb", isDefault: false },
  { code: "ar", nativeName: "العربية", direction: "rtl", flagCountryCode: "sa", isDefault: false },
];
let pendingLanguages: Promise<StoreLanguage[]> | null = null;
export function getStoreLanguages(): Promise<StoreLanguage[]> {
  if (!pendingLanguages) {
    pendingLanguages = http.getResource("/catalog/languages").catch(function (error) {
      pendingLanguages = null;
      throw error;
    });
  }
  return pendingLanguages!;
}

export default function LanguageSelector() {
  const language = useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicLogin = location.pathname === "/login" || location.pathname === "/register";
  const [languages, setLanguages] = useState<StoreLanguage[]>(function () {
    return isPublicLogin ? publicLoginLanguages : [];
  });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const locked = useRef(false);
  useEffect(function () {
    let active = true;
    setFailed(false);
    getStoreLanguages().then(function (available) {
      if (!active) return;
      configureLanguages(available, new URLSearchParams(location.search).get("language") || undefined);
      setLanguages(available);
    }).catch(function (error) { if (active) { setFailed(true); AppFeedback.httpError(error); } });
    return function () { active = false; };
  }, [location.search]);

  async function change(code: string) {
    if (locked.current || code === language) return;
    locked.current = true;
    setBusy(true);
    try {
      if (getAgencySession() && !isPublicLogin) {
        const bootstrap = await http.getResource("/catalog/agency-account/bootstrap");
        await http.putasync("/catalog/languages/preference", { code }, { headers: { "X-Portal-Csrf": bootstrap.csrfToken } });
      }
      activateLanguage(code);
      if (isPublicLogin && new URLSearchParams(location.search).has("language")) {
        const search = new URLSearchParams(location.search);
        search.set("language", code);
        navigate({ pathname: location.pathname, search: `?${search.toString()}` }, { replace: true, state: location.state });
      }
    } catch (error) { AppFeedback.httpError(error); }
    finally { locked.current = false; setBusy(false); }
  }

  if ((!isPublicLogin && failed) || languages.length <= 1) {
    return null;
  }
  return (
    <Select
      className="store-language-selector"
      popupClassName="store-language-menu"
      aria-label={t("زبان")}
      value={language}
      loading={busy || !languages.length}
      disabled={busy || !languages.length}
      onChange={change}
      suffixIcon={<GlobalOutlined />}
      popupMatchSelectWidth={180}
      options={languages.map(function (item) {
        return {
          value: item.code,
          label: (
            <span className="store-language-option">
              {item.flagCountryCode && flags[item.flagCountryCode] ? (
                <img
                  className="store-language-flag"
                  src={flags[item.flagCountryCode]}
                  alt=""
                  aria-hidden="true"
                  width={23}
                  height={17}
                />
              ) : <GlobalOutlined aria-hidden="true" />}
              <span lang={item.code} dir={item.direction}>{item.nativeName}</span>
            </span>
          ),
        };
      })}
    />
  );
}
